const DELIVERY_TIMEOUT_MS = 30000;
const ATTRIBUTION_DAYS = 30;

function safeStorageGet(key) {
    try {
        return window.sessionStorage.getItem(key) || '';
    } catch (error) {
        return '';
    }
}

function safeStorageSet(key, value) {
    try {
        window.sessionStorage.setItem(key, String(value));
    } catch (error) {}
}

function createSessionId(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return prefix + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 12);
}

function normalizeToken(value, fallback = 'unknown') {
    const normalized = String(value || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
    return normalized || fallback;
}

function domainFunnelTracking() {
    const rawConfig = window.growtypeDomainFunnelTrackingConfig;
    if (!rawConfig || typeof rawConfig !== 'object') return;

    const funnelId = normalizeToken(rawConfig.funnel_id, 'funnel');
    const funnelVersion = normalizeToken(rawConfig.funnel_version, 'v1');
    const trackingDriver = rawConfig.tracking_driver === 'custom' ? 'custom' : 'generic';
    const eventPrefix = (trackingDriver === 'custom' ? '' : 'funnel_') + funnelId + '_';
    const sessionKey = 'growtype-funnel-session-' + funnelId;
    const maxQuestionKey = sessionKey + '-max-question';
    const startedAt = Date.now();
    const queue = [];
    let queueTimer = null;
    let queueDeadlineTimer = null;
    let exitTracked = false;
    let progressionTarget = '';
    let lastQuestionToken = '';

    function sessionId() {
        let value = safeStorageGet(sessionKey);
        if (!value) {
            value = createSessionId(funnelId);
            safeStorageSet(sessionKey, value);
        }
        return value;
    }

    function currentStage() {
        const root = document.querySelector('[data-funnel-stage]');
        if (root) return normalizeToken(root.getAttribute('data-funnel-stage'));
        if (new URL(window.location.href).searchParams.has('experience')) return 'quiz';
        return normalizeToken(rawConfig.funnel_stage, 'landing');
    }

    function setAttribution() {
        let context = {};
        const match = document.cookie.match(/(?:^|; )ga_attribution_context=([^;]*)/);
        if (match) {
            try {
                context = JSON.parse(decodeURIComponent(match[1])) || {};
            } catch (error) {
                context = {};
            }
        }
        context.recommendation_rule = funnelId + '_funnel';
        context.recommendation_rule_version = funnelVersion;
        context.recommendation_source = funnelId;
        context.funnel_session_id = sessionId();
        context.character_attribution = 'none';
        const expires = new Date(Date.now() + ATTRIBUTION_DAYS * 86400000).toUTCString();
        document.cookie = 'ga_attribution_context=' + encodeURIComponent(JSON.stringify(context))
            + '; path=/; expires=' + expires + '; SameSite=Lax'
            + (window.location.protocol === 'https:' ? '; Secure' : '');
    }

    function baseMetadata(extra = {}) {
        return Object.assign({
            funnel_id: funnelId,
            funnel_version: funnelVersion,
            funnel_session_id: sessionId(),
            funnel_stage: currentStage(),
            funnel_module: normalizeToken(rawConfig.module, funnelId),
        }, extra);
    }

    function drainQueue() {
        if (!queue.length) {
            if (queueTimer) window.clearInterval(queueTimer);
            queueTimer = null;
            if (queueDeadlineTimer) window.clearTimeout(queueDeadlineTimer);
            queueDeadlineTimer = null;
            return;
        }
        const pending = queue.filter((item) => {
            if (!item.firstPartySent && typeof window.gaTrack === 'function') {
                window.gaTrack(item.eventName, {
                    object_id: funnelId,
                    object_type: 'funnel',
                    metadata: item.metadata,
                });
                item.firstPartySent = true;
            }
            if (!item.externalSent && typeof window.growtypeAnalyticsCapture === 'function') {
                window.growtypeAnalyticsCapture(item.eventName, item.metadata);
                item.externalSent = true;
            }
            return !item.firstPartySent || !item.externalSent;
        });
        queue.splice(0, queue.length, ...pending);
    }

    function track(action, extra = {}) {
        queue.push({
            eventName: eventPrefix + normalizeToken(action),
            metadata: baseMetadata(extra),
            firstPartySent: false,
            externalSent: false,
        });
        drainQueue();
        if (queue.length && !queueTimer) {
            queueTimer = window.setInterval(drainQueue, 100);
            queueDeadlineTimer = window.setTimeout(() => {
                if (queueTimer) window.clearInterval(queueTimer);
                queueTimer = null;
                queueDeadlineTimer = null;
            }, DELIVERY_TIMEOUT_MS);
        }
    }

    function questionDetails(value) {
        const element = value?.nodeType === 1 ? value : value?.[0]?.nodeType === 1 ? value[0] : null;
        return {
            question_number: Number(element?.getAttribute('data-question-nr') || 0),
            question_key: String(element?.getAttribute('data-key') || '').slice(0, 80),
        };
    }

    function funnelQuizRoot() {
        return Array.from(document.querySelectorAll('[data-quiz-slug]')).find((element) => {
            return normalizeToken(element.getAttribute('data-quiz-slug'), '') === funnelId;
        }) || null;
    }

    function isFunnelQuestion(value) {
        const element = value?.nodeType === 1 ? value : value?.[0]?.nodeType === 1 ? value[0] : null;
        const root = funnelQuizRoot();
        return Boolean(element && root && root.contains(element));
    }

    function updateMaxQuestion(number) {
        const current = Number(safeStorageGet(maxQuestionKey) || 0);
        const next = Math.max(current, Number(number || 0));
        if (next > current) safeStorageSet(maxQuestionKey, next);
        return next;
    }

    function trackQuestionView(question, navigation = 'initial') {
        const details = questionDetails(question);
        if (!details.question_number && !details.question_key) return;
        const token = details.question_number + ':' + details.question_key;
        if (token === lastQuestionToken) return;
        lastQuestionToken = token;
        const metadata = Object.assign(details, {
            navigation,
            max_question_viewed: updateMaxQuestion(details.question_number),
        });
        track('question_viewed', metadata);
        track('question_' + normalizeToken(details.question_number || details.question_key) + '_viewed', metadata);
    }

    window.growtypeDomainFunnelTracking = {
        funnelId,
        funnelVersion,
        sessionId: sessionId(),
        track,
    };

    const waitingEvents = Array.isArray(window.growtypeFunnelTrackingQueue)
        ? window.growtypeFunnelTrackingQueue
        : [];
    window.growtypeFunnelTrackingQueue = waitingEvents.filter((item) => {
        if (!item || normalizeToken(item.funnelId, '') !== funnelId) return true;
        track(item.action, item.metadata || {});
        return false;
    });

    setAttribution();
    if (trackingDriver === 'custom') return;

    document.addEventListener('growtypeQuizShowQuestion', (event) => {
        const detail = event.detail || {};
        if (!isFunnelQuestion(detail.currentQuestion) && !isFunnelQuestion(detail.previousQuestion)) return;
        const answered = detail.answer_details || {};
        if (answered.question) {
            const answerMetadata = {
                question_number: Number(answered.question_number || 0),
                question_key: String(answered.question || '').slice(0, 80),
                answer_count: Array.isArray(answered.answer) ? answered.answer.length : (answered.answer ? 1 : 0),
            };
            track('question_answered', answerMetadata);
            track('question_' + normalizeToken(answerMetadata.question_number || answerMetadata.question_key) + '_answered', answerMetadata);
        }
        const previous = questionDetails(detail.previousQuestion);
        const current = questionDetails(detail.currentQuestion);
        trackQuestionView(detail.currentQuestion, previous.question_number && current.question_number < previous.question_number ? 'back' : 'forward');
    });

    document.addEventListener('growtypeQuizSaveQuizData', (event) => {
        if (!funnelQuizRoot()) return;
        const answers = event.detail?.answers || {};
        progressionTarget = 'results';
        track('quiz_completed', {
            answered_question_count: Object.keys(answers).length,
            max_question_viewed: Number(safeStorageGet(maxQuestionKey) || 0),
        });
    });

    document.addEventListener('submit', (event) => {
        if (!event.target.closest('form')) return;
        progressionTarget = 'form_destination';
        track('form_submitted', {
            form_id: normalizeToken(event.target.id || event.target.getAttribute('name') || 'form'),
        });
    }, true);

    document.addEventListener('click', (event) => {
        const checkout = event.target.closest('.growtype-wc-checkout-button, .btn-addtocart, a[href*="add-to-cart"]');
        if (checkout) {
            progressionTarget = 'checkout';
            track('checkout_clicked', {
                product_id: String(checkout.dataset.productId || '').slice(0, 32),
                destination_stage: 'checkout',
            });
            return;
        }

        const target = event.target.closest('[data-funnel-action], a[href]');
        if (!target) return;
        const href = target.getAttribute('href') || '';
        if (href.startsWith('#')) return;
        let destinationPath = '';
        let destinationType = 'action';
        if (href) {
            try {
                const destination = new URL(href, window.location.href);
                destinationPath = destination.origin === window.location.origin ? destination.pathname : '';
                destinationType = destination.origin === window.location.origin ? 'internal' : 'external';
                progressionTarget = destinationPath || destinationType;
            } catch (error) {}
        }
        track('cta_clicked', {
            action: normalizeToken(target.getAttribute('data-funnel-action') || 'navigation'),
            destination_path: destinationPath.slice(0, 255),
            destination_type: destinationType,
        });
    }, true);

    window.addEventListener('pagehide', () => {
        if (exitTracked) return;
        exitTracked = true;
        const exitMetadata = {
            destination_stage: String(progressionTarget).slice(0, 255),
            progressed: progressionTarget !== '',
            time_on_stage_seconds: Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
            max_question_viewed: Number(safeStorageGet(maxQuestionKey) || 0),
        };
        track('stage_exited', exitMetadata);
        track('stage_' + currentStage() + '_exited', exitMetadata);
        drainQueue();
    }, { capture: true });

    const stageMetadata = {
        entry_path: window.location.pathname,
        max_question_viewed: Number(safeStorageGet(maxQuestionKey) || 0),
    };
    track('stage_viewed', stageMetadata);
    track('stage_' + currentStage() + '_viewed', stageMetadata);
    trackQuestionView(funnelQuizRoot()?.querySelector('.growtype-quiz-question.is-active'));
}

export { domainFunnelTracking };
